import type { Collector, ProjectTask } from '@/types/odoo';
import type { DashboardFase } from '@/api/projectFases';

/**
 * Pure helpers for the dashboard fase tabs + status strip (Fase 2).
 * The collector→fase mapping is resolved server-side (materiaallijst-based);
 * these functions only group/aggregate what the backend already mapped.
 */

/** First fase whose date_deadline is today or later; falls back to the first fase. */
export function nearestFutureFase(fases: DashboardFase[]): number | null {
  if (fases.length === 0) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
  const future = fases.filter((f) => String(f.date_deadline).slice(0, 10) >= todayStr);
  return (future[0] ?? fases[0]).id;
}

/** Group collectors by their server-resolved fase id. Unmapped collectors are dropped. */
export function groupCollectorsByFase(
  collectors: Collector[],
  collectorFases: Record<number, number>,
): Map<number, Collector[]> {
  const map = new Map<number, Collector[]>();
  for (const c of collectors) {
    const faseId = collectorFases[c.id];
    if (faseId === undefined) continue;
    const arr = map.get(faseId) ?? [];
    arr.push(c);
    map.set(faseId, arr);
  }
  return map;
}

export interface SystemMixEntry {
  systeem: string;
  /** Total m² of every collector on this systeem. */
  m2: number;
  /** m² of the op-druk (uitgevoerd) collectors on this systeem — never exceeds m2. */
  uitgevoerdM2: number;
}

export interface FaseAggregate {
  count: number;
  uitgevoerd: number; // collectors op druk
  totalM2: number;
  uitgevoerdM2: number;
  /** Sorted by total m² desc. Σ m2 === totalM2 and Σ uitgevoerdM2 === uitgevoerdM2
   *  by construction, so a per-systeem breakdown always adds up to its headline. */
  systemMix: SystemMixEntry[];
}

export function faseAggregate(collectors: Collector[]): FaseAggregate {
  let totalM2 = 0;
  let uitgevoerd = 0;
  let uitgevoerdM2 = 0;
  const systems = new Map<string, { m2: number; uitgevoerdM2: number }>();
  for (const c of collectors) {
    const m2 = c.x_studio_collector_m2 || 0;
    const opDruk = Boolean(c.x_studio_collector_op_druk);
    totalM2 += m2;
    if (opDruk) {
      uitgevoerd += 1;
      uitgevoerdM2 += m2;
    }
    const sys = c.x_studio_collector_systeem || 'Onbekend';
    const entry = systems.get(sys) ?? { m2: 0, uitgevoerdM2: 0 };
    entry.m2 += m2;
    if (opDruk) entry.uitgevoerdM2 += m2;
    systems.set(sys, entry);
  }
  const systemMix = [...systems.entries()]
    .map(([systeem, v]) => ({ systeem, m2: v.m2, uitgevoerdM2: v.uitgevoerdM2 }))
    .sort((a, b) => b.m2 - a.m2);
  return { count: collectors.length, uitgevoerd, totalM2, uitgevoerdM2, systemMix };
}

export interface RoundedSystemMixEntry {
  systeem: string;
  /** Whole m² — Σ over the returned lines === Math.round(totalM2). */
  m2: number;
  /** Whole m² — Σ over the returned lines === Math.round(uitgevoerdM2). */
  uitgevoerdM2: number;
}

/**
 * Round `values` to whole units that sum EXACTLY to `target` (largest remainder).
 * Every part stays within 1 of its raw value and is never negative. Ties are
 * broken by the larger raw value, then by input order, so the output is stable.
 */
function apportionWhole(values: number[], target: number): number[] {
  if (values.length === 0) return [];
  const out = values.map((v) => Math.max(0, Math.floor(v)));
  const order = values
    .map((v, i) => ({ i, frac: v - Math.floor(v), v }))
    .sort((a, b) => b.frac - a.frac || b.v - a.v || a.i - b.i)
    .map((e) => e.i);
  let diff = target - out.reduce((s, n) => s + n, 0);
  // Hand the leftover whole units to the largest fractional parts first. The
  // modulo only matters for inputs where target exceeds Σ⌈value⌉, which the two
  // call sites below cannot produce — it is a guarantee, not an expected path.
  for (let k = 0; diff > 0; k += 1) {
    out[order[k % order.length]] += 1;
    diff -= 1;
  }
  // …and take units back from the smallest fractional parts, never below zero.
  for (let k = order.length - 1; diff < 0 && k >= 0; k -= 1) {
    const i = order[k];
    if (out[i] > 0) {
      out[i] -= 1;
      diff += 1;
    }
  }
  return out;
}

/**
 * Print-ready systeem lines whose PRINTED numbers still add up to the PRINTED
 * headline. Rounding each line on its own does not: two collectoren of 61.5 and
 * 111.5 m² give a tile of Math.round(173) = "173" over lines "62" + "112" = 174,
 * and the breakdown contradicts the number it is supposed to explain. Fractional
 * collector m² is real in this data, so this is not a theoretical case.
 *
 * Largest-remainder apportionment instead: each line moves by less than 1 m² and
 * the column sums exactly. The totals are passed in (rather than re-summed here)
 * so the uitgevoerd column is tied to the very number the caller prints.
 *
 * The "totaal" column is apportioned as uitgevoerd + rest (rest = m2 −
 * uitgevoerdM2), which keeps a printed uitgevoerd from ever exceeding the printed
 * totaal on its own line.
 */
export function roundSystemMix(
  mix: SystemMixEntry[],
  uitgevoerdM2: number,
  totalM2: number,
): RoundedSystemMixEntry[] {
  const roundedUitgevoerd = Math.round(uitgevoerdM2);
  const uitgevoerdParts = apportionWhole(mix.map((s) => s.uitgevoerdM2), roundedUitgevoerd);
  const restParts = apportionWhole(
    mix.map((s) => Math.max(0, s.m2 - s.uitgevoerdM2)),
    Math.round(totalM2) - roundedUitgevoerd,
  );
  return mix.map((s, i) => ({
    systeem: s.systeem,
    m2: uitgevoerdParts[i] + restParts[i],
    uitgevoerdM2: uitgevoerdParts[i],
  }));
}

/**
 * Date range [from, to] for a fase, derived from its non-collector planning
 * children in projectTasks (parent_id === faseId). Mirrors the backend
 * fetchProjectFases grouping so the same fase parents are used. null if none.
 */
export function faseDateRange(faseId: number, projectTasks: ProjectTask[]): { from: string; to: string } | null {
  const dates: string[] = [];
  for (const t of projectTasks) {
    const parentId = Array.isArray(t.parent_id) ? t.parent_id[0] : null;
    if (parentId !== faseId) continue;
    if (t.x_studio_collector) continue;
    if (!t.date_deadline) continue;
    dates.push(String(t.date_deadline).slice(0, 10));
  }
  if (dates.length === 0) return null;
  dates.sort();
  return { from: dates[0], to: dates[dates.length - 1] };
}

/** Derived fase status from collector progress (no separate Odoo stage read needed). */
export type FaseStatus = 'gepland' | 'in_uitvoering' | 'uitgevoerd';

export function faseStatus(agg: FaseAggregate): FaseStatus {
  if (agg.count === 0 || agg.uitgevoerd === 0) return 'gepland';
  if (agg.uitgevoerd >= agg.count) return 'uitgevoerd';
  return 'in_uitvoering';
}
